import type { Item, Session, StudyMode } from './types';

/** Fisher–Yates. `rng` is injectable so tests are deterministic. */
export function shuffle<T>(rows: readonly T[], rng: () => number = Math.random): T[] {
  const out = rows.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

export interface SelectOptions {
  mode: StudyMode;
  /** Required for the class and chapter modes. */
  sessionId?: string;
  /** flashcard for the card view, question for the quiz view. */
  want: 'flashcard' | 'question';
  limit?: number;
  /** Cumulative only: weight the newest class session first. */
  focusLatestClass?: boolean;
  /**
   * Cumulative only. Textbook chapters are left out by default: there is far
   * more of the textbook than of his own classes, and mixing it in would bury
   * what he was actually taught.
   */
  includeTextbook?: boolean;
  sessions: Session[];
  rng?: () => number;
}

const isQuestion = (item: Item) => item.type !== 'flashcard';

export function matchesWant(item: Item, want: 'flashcard' | 'question'): boolean {
  return want === 'flashcard' ? item.type === 'flashcard' : isQuestion(item);
}

/** The pool a mode draws from, before shuffling or limiting. */
export function poolForMode(items: Item[], options: SelectOptions): Item[] {
  const { mode, sessionId, want, sessions, includeTextbook } = options;
  const byId = new Map(sessions.map((s) => [s.id, s]));
  const usable = items.filter((i) => !i.deleted && matchesWant(i, want) && byId.has(i.sessionId));

  if (isSingleSessionMode(mode)) {
    if (!sessionId) return [];
    return usable.filter((i) => i.sessionId === sessionId);
  }
  // Cumulative draws from every class session and every chapter review, and
  // from the textbook only when he asks for it.
  if (includeTextbook) return usable;
  return usable.filter((i) => byId.get(i.sessionId)?.kind !== 'textbook');
}

/** Modes that study one chosen session whole, in its imported order. */
export function isSingleSessionMode(mode: StudyMode): boolean {
  return mode === 'class' || mode === 'chapter' || mode === 'textbook';
}

export function selectItems(items: Item[], options: SelectOptions): Item[] {
  const pool = poolForMode(items, options);
  const rng = options.rng ?? Math.random;

  if (isSingleSessionMode(options.mode)) {
    // A single session is studied in its imported order.
    return pool.slice().sort((a, b) => a.order - b.order);
  }

  let ordered = shuffle(pool, rng);

  if (options.focusLatestClass) {
    const latest = latestClassId(options.sessions);
    if (latest) {
      const front = ordered.filter((i) => i.sessionId === latest);
      const rest = ordered.filter((i) => i.sessionId !== latest);
      ordered = [...front, ...rest];
    }
  }

  return options.limit ? ordered.slice(0, options.limit) : ordered;
}

export function latestClassId(sessions: Session[]): string | undefined {
  const classes = sessions.filter((s) => s.kind === 'class' && !s.deleted);
  if (classes.length === 0) return undefined;
  const sorted = classes.slice().sort((a, b) => {
    const at = Date.parse(a.addedAt);
    const bt = Date.parse(b.addedAt);
    if (Number.isNaN(at) || Number.isNaN(bt)) return 0;
    return bt - at;
  });
  return sorted[0]?.id;
}
